/* eslint-disable */
/**
 * Jack suspension packing analyzer — extracted from public/jack-benchmark.html.
 * Regenerate: node scripts/build-jack-engine.mjs
 */

export const JACK_DEFAULT_PARAMS = {
  frontTravel: 150,
  rearTravel: 150,
  sagPercent: 30,
  frontPotStroke: 225,
  rearPotStroke: 85,
  rearA: 0,
  rearB: 2.8574,
  rearC: 0,
  rearD: 0,
  trimStart: 0,
  trimEnd: Infinity,
  invertFront: false,
  invertRear: false,
  medianFilterSize: 5,
  maxPosChange: 15,
  velocityWindow: 21,
  filterType: 'savgol',
  positionFilterWindow: 5,
  velocityFilterWindow: 11,
  showRawData: false,
  minStrokeDepth: 5,
  minStrokeSamples: 5,
  bottomOutThreshold: 95,
  topOutThreshold: 5,
  extensionThreshold: 15,
  packedThreshold: 20,
  minPackedHits: 3,
  stuckThreshold: 8,
  ratchetDelta: 5,
  velocityThreshold: 50,
};

let _jackRuntimeParams = { ...JACK_DEFAULT_PARAMS };

function pickFinite(...vals) {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function getFiniteMinMax(values) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const n = Number(values[i]);
    if (!Number.isFinite(n)) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  if (min === Infinity || max === -Infinity) return null;
  return { min, max };
}

function getFiniteMax(values) {
  const range = getFiniteMinMax(values);
  return range ? range.max : null;
}

function sanitizeJackParams(partialParams = {}) {
  const merged = { ...JACK_DEFAULT_PARAMS, ...(partialParams || {}) };
  const positiveKeys = [
    'frontTravel', 'rearTravel', 'frontPotStroke', 'rearPotStroke',
    'medianFilterSize', 'maxPosChange', 'velocityWindow', 'positionFilterWindow', 'velocityFilterWindow',
    'minStrokeDepth', 'minStrokeSamples', 'bottomOutThreshold', 'topOutThreshold', 'extensionThreshold',
    'packedThreshold', 'minPackedHits', 'stuckThreshold', 'ratchetDelta', 'velocityThreshold',
  ];
  for (const key of positiveKeys) {
    const n = Number(merged[key]);
    if (!Number.isFinite(n) || n <= 0) {
      merged[key] = JACK_DEFAULT_PARAMS[key];
    }
  }
  const finiteKeys = ['rearA', 'rearB', 'rearC', 'rearD', 'trimStart', 'trimEnd', 'sagPercent'];
  for (const key of finiteKeys) {
    const n = Number(merged[key]);
    if (!Number.isFinite(n)) {
      merged[key] = JACK_DEFAULT_PARAMS[key];
    } else {
      merged[key] = n;
    }
  }
  merged.invertFront = Boolean(merged.invertFront);
  merged.invertRear = Boolean(merged.invertRear);
  return merged;
}

/**
 * Snapshot of params the Jack engine uses after `sanitizeJackParams` (same as `runJackPackingAnalysis` entry).
 * Sorted keys, `trimEnd: Infinity` as string for JSON-safe comparison with benchmark logs.
 */
export function buildJackInputDebugSnapshot(partialParams = {}) {
  const sanitized = sanitizeJackParams(partialParams || {});
  const sorted = {};
  for (const k of Object.keys(sanitized).sort()) {
    const v = sanitized[k];
    if (v === Infinity) sorted[k] = 'Infinity';
    else if (v === -Infinity) sorted[k] = '-Infinity';
    else sorted[k] = v;
  }
  return sorted;
}

function getParams() {
  return _jackRuntimeParams;
}

let rawData = [];
let processedData = [];
let impacts = { front: [], rear: [] };
let packingEvents = { front: [], rear: [] };
let analysisResults = null;

function showStatus(_msg, _type) {}

function debugLog(msg) {
  if (typeof process !== 'undefined' && process.env?.REACT_APP_JACK_DEBUG === '1') {
    console.log(msg);
  }
}

function clearDebug() {}

// Sort raw data by time column (handles multiple possible column names)
function sortRawDataByTime() {
  if (rawData.length === 0) return;
  
  // Find the time column
  const sample = rawData[0];
  const timeCol = ['time', 'Time', 'TIME', 't', 'timestamp', 'seconds', 'micros', 'Micros'].find(col => 
    sample.hasOwnProperty(col) && sample[col] !== null && sample[col] !== undefined
  );
  
  if (!timeCol) {
    console.log('No time column found, skipping sort');
    return;
  }
  
  console.log('Sorting raw data by column:', timeCol);
  
  // Sort by time column
  rawData.sort((a, b) => {
    const aTime = Number(a[timeCol]);
    const bTime = Number(b[timeCol]);
    
    // Handle NaN or invalid values
    if (isNaN(aTime)) return 1;
    if (isNaN(bTime)) return -1;
    
    return aTime - bTime;
  });
  
  console.log('Raw data sorted. First time:', rawData[0][timeCol], 'Last time:', rawData[rawData.length-1][timeCol]);
}


// ==================== SIGNAL FILTERING ====================

// Gap detection threshold (milliseconds) - gaps larger than this are treated as discontinuities
const GAP_THRESHOLD_MS = 10;

// Find indices where time gaps occur (returns array of indices AFTER each gap)
function findGapIndices(times, thresholdSec) {
  const gaps = [];
  for (let i = 1; i < times.length; i++) {
    if ((times[i] - times[i - 1]) > thresholdSec) {
      gaps.push(i);
    }
  }
  return gaps;
}

// Apply filter to data, respecting time gaps (don't smooth across gaps)
// This prevents artificial position interpolation at discontinuities
function applyFilterWithGaps(arr, times, filterFn, windowSize) {
  const gapThreshold = GAP_THRESHOLD_MS / 1000; // Convert to seconds
  const gaps = findGapIndices(times, gapThreshold);

  if (gaps.length === 0) {
    // No gaps - apply filter normally
    return filterFn(arr, windowSize);
  }

  debugLog('Gap-aware filtering: found ' + gaps.length + ' time gaps (>' + GAP_THRESHOLD_MS + 'ms)');

  // Log gap locations with times
  for (let g = 0; g < Math.min(gaps.length, 5); g++) {
    const gapIdx = gaps[g];
    const beforeTime = times[gapIdx - 1];
    const afterTime = times[gapIdx];
    const gapSize = ((afterTime - beforeTime) * 1000).toFixed(1);
    debugLog('  Gap ' + (g+1) + ': index ' + gapIdx + ', time ' + beforeTime.toFixed(4) + 's -> ' + afterTime.toFixed(4) + 's (' + gapSize + 'ms)');
  }
  if (gaps.length > 5) {
    debugLog('  ... and ' + (gaps.length - 5) + ' more gaps');
  }

  // Split into segments at gaps
  const segments = [];
  let segStart = 0;
  for (const gapIdx of gaps) {
    if (gapIdx > segStart) {
      segments.push({ start: segStart, end: gapIdx });
    }
    segStart = gapIdx;
  }
  // Add final segment
  if (segStart < arr.length) {
    segments.push({ start: segStart, end: arr.length });
  }

  debugLog('  Created ' + segments.length + ' segments, total points: ' + arr.length);

  // Filter each segment independently
  const result = new Array(arr.length);
  let filledCount = 0;

  for (const seg of segments) {
    const segLen = seg.end - seg.start;
    const segData = arr.slice(seg.start, seg.end);

    // Only apply filter if segment is large enough
    const filtered = segLen >= windowSize ?
      filterFn(segData, windowSize) : segData.slice();

    // Verify filter didn't change array length
    if (filtered.length !== segData.length) {
      debugLog('[ERROR] Filter changed segment length! Was ' + segData.length + ', now ' + filtered.length);
    }

    for (let i = 0; i < filtered.length; i++) {
      result[seg.start + i] = filtered[i];
      filledCount++;
    }
  }

  // Verify all positions were filled
  if (filledCount !== arr.length) {
    debugLog('[ERROR] Gap-aware filter data loss! Input: ' + arr.length + ', filled: ' + filledCount);
  }

  return result;
}

// Find robust baseline using percentile method
// Uses 2nd percentile to ignore brief outlier dips while finding consistent minimum
function findRobustBaseline(arr) {
  if (arr.length === 0) return 0;

  // Sort a copy to find percentiles
  const sorted = arr.slice().sort((a, b) => a - b);

  // Use 2nd percentile as robust minimum (ignores bottom 2% as outliers)
  const percentileIndex = Math.floor(sorted.length * 0.02);
  const robustMin = sorted[Math.max(0, percentileIndex)];

  // Also check if the "minimum zone" is consistent
  // Look at bottom 5% of values and see if they cluster together
  const bottom5Percent = sorted.slice(0, Math.max(10, Math.floor(sorted.length * 0.05)));
  const bottom5Mean = bottom5Percent.reduce((a, b) => a + b, 0) / bottom5Percent.length;
  const bottom5Std = Math.sqrt(bottom5Percent.reduce((sum, v) => sum + (v - bottom5Mean) ** 2, 0) / bottom5Percent.length);

  // If bottom values are very spread out (high std), use a higher percentile
  // This handles cases where there are many scattered outliers
  if (bottom5Std > 10) {
    // Use 5th percentile instead for noisy data
    const saferIndex = Math.floor(sorted.length * 0.05);
    return sorted[Math.max(0, saferIndex)];
  }

  return robustMin;
}

// Median filter for spike removal (sliding window)
function medianFilter(arr, windowSize) {
  const halfWindow = Math.floor(windowSize / 2);
  const result = new Array(arr.length);

  for (let i = 0; i < arr.length; i++) {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(arr.length - 1, i + halfWindow);
    const window = [];
    for (let j = start; j <= end; j++) {
      window.push(arr[j]);
    }
    window.sort((a, b) => a - b);
    result[i] = window[Math.floor(window.length / 2)];
  }
  return result;
}

// Limit position change between samples (remove impossible jumps)
function limitPositionChange(arr, maxChange) {
  const result = new Array(arr.length);
  result[0] = arr[0];

  for (let i = 1; i < arr.length; i++) {
    const delta = arr[i] - result[i - 1];
    if (Math.abs(delta) > maxChange) {
      result[i] = result[i - 1] + Math.sign(delta) * maxChange;
    } else {
      result[i] = arr[i];
    }
  }
  return result;
}

// Savitzky-Golay smoothing coefficients (deriv=0 only)
// For velocity, we use a more robust approach: diff then smooth
function getSavitzkyGolayCoeffs(windowSize, polyOrder, deriv) {
  const halfWindow = Math.floor(windowSize / 2);
  const coeffs = new Array(windowSize);

  // Only support smoothing (deriv=0) - velocity uses diff-then-smooth approach
  // Parabolic weighting gives good smoothing while preserving peaks
  let weightSum = 0;
  for (let i = 0; i < windowSize; i++) {
    const x = i - halfWindow;
    // Parabolic weight: 1 - (x/halfWindow)^2, minimum 0.1 at edges
    const weight = Math.max(0.1, 1 - (x / halfWindow) * (x / halfWindow));
    coeffs[i] = weight;
    weightSum += weight;
  }
  // Normalize to sum to 1
  for (let i = 0; i < windowSize; i++) {
    coeffs[i] /= weightSum;
  }

  return coeffs;
}

// Apply Savitzky-Golay smoothing filter (deriv=0 only)
function savitzkyGolayFilter(arr, windowSize, polyOrder, deriv, sampleRate) {
  const halfWindow = Math.floor(windowSize / 2);
  const coeffs = getSavitzkyGolayCoeffs(windowSize, polyOrder, 0); // Always smoothing
  const result = new Array(arr.length);

  // Handle edges with proper normalization
  for (let i = 0; i < arr.length; i++) {
    let sum = 0;
    let coeffSum = 0;

    for (let j = 0; j < windowSize; j++) {
      const dataIndex = i + j - halfWindow;
      if (dataIndex >= 0 && dataIndex < arr.length) {
        sum += coeffs[j] * arr[dataIndex];
        coeffSum += coeffs[j]; // Track actual coefficients used
      }
    }

    // Normalize by actual coefficient sum (important at edges)
    result[i] = coeffSum > 0 ? sum / coeffSum : arr[i];
  }

  return result;
}

// Combined position filtering: median + limit
function filterPosition(arr, medianSize, maxChange) {
  let filtered = medianFilter(arr, medianSize);
  filtered = limitPositionChange(filtered, maxChange);
  return filtered;
}

// Apply user-selected filter type to data array
function applyDataFilter(arr, windowSize) {
  const params = getParams();
  const filterType = params.filterType || 'savgol';

  // Ensure odd window size
  let win = Math.max(1, Math.floor(windowSize));
  if (win % 2 === 0) win += 1;

  if (filterType === 'none') {
    return arr.slice(); // Return copy of original
  } else if (filterType === 'moving_average') {
    return movingAverageFilter(arr, win);
  } else {
    // Default: Savitzky-Golay smoothing (deriv=0)
    return savitzkyGolayFilter(arr, win, 3, 0, 1);
  }
}

// Moving average filter
function movingAverageFilter(arr, windowSize) {
  const halfWindow = Math.floor(windowSize / 2);
  const result = new Array(arr.length);

  for (let i = 0; i < arr.length; i++) {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(arr.length - 1, i + halfWindow);
    let sum = 0;
    let count = 0;
    for (let j = start; j <= end; j++) {
      sum += arr[j];
      count++;
    }
    result[i] = sum / count;
  }
  return result;
}

// Calculate velocity with filtering using actual time differences
// Robust approach: calculate raw velocity, handle gaps, remove spikes, then smooth
function calculateFilteredVelocity(positions, times, windowSize) {
  const params = getParams();
  const filterType = params.filterType || 'savgol';

  // Ensure odd window size
  let win = Math.max(3, Math.floor(windowSize));
  if (win % 2 === 0) win += 1;

  // Maximum reasonable velocity for mountain bike suspension (mm/s)
  // Pro DH bikes rarely exceed 3000 mm/s even on huge hits
  const MAX_VELOCITY = 5000;

  // Expected sample interval (1ms at 1000Hz)
  const EXPECTED_DT = 0.001;
  // Gap threshold: if dt > 10x expected, it's a gap
  const GAP_THRESHOLD = EXPECTED_DT * 10; // 10ms

  // Debug: analyze time intervals
  let minDt = Infinity, maxDt = 0, sumDt = 0;
  let smallDtCount = 0, zeroDtCount = 0, gapCount = 0;
  for (let i = 1; i < Math.min(times.length, 1000); i++) {
    const dt = times[i] - times[i - 1];
    if (dt <= 0) zeroDtCount++;
    else if (dt < 0.0001) smallDtCount++; // < 0.1ms
    else if (dt > GAP_THRESHOLD) gapCount++;
    minDt = Math.min(minDt, dt);
    maxDt = Math.max(maxDt, dt);
    sumDt += dt;
  }
  const avgDt = sumDt / (Math.min(times.length, 1000) - 1);
  debugLog('Time analysis: avgDt=' + (avgDt*1000).toFixed(3) + 'ms, minDt=' + (minDt*1000).toFixed(3) + 'ms, maxDt=' + (maxDt*1000).toFixed(3) + 'ms');
  debugLog('  Zero/negative dt: ' + zeroDtCount + ', Very small dt: ' + smallDtCount + ', Gaps (>10ms): ' + gapCount);

  // Step 1: Calculate raw velocity, marking gaps as invalid
  const rawVel = new Array(positions.length);
  const isGap = new Array(positions.length).fill(false);
  rawVel[0] = 0;

  let spikeCount = 0;
  for (let i = 1; i < positions.length; i++) {
    const dt = times[i] - times[i - 1];
    const dp = positions[i] - positions[i - 1];

    if (dt > GAP_THRESHOLD) {
      // This is a gap - velocity during gap is unreliable
      isGap[i] = true;
      rawVel[i] = dp / dt;
    } else if (dt > 0.0001) {
      // Normal sample
      rawVel[i] = dp / dt;
    } else {
      // dt too small - carry forward
      rawVel[i] = rawVel[i - 1];
    }

    // Log any suspicious large velocities
    if (Math.abs(rawVel[i]) > 2500 && spikeCount < 10) {
      spikeCount++;
      debugLog('[SPIKE] i=' + i + ' t=' + times[i].toFixed(4) + ' dt=' + (dt*1000).toFixed(2) + 'ms dp=' + dp.toFixed(2) + 'mm vel=' + rawVel[i].toFixed(0) + 'mm/s');
    }
  }

  // Step 1b: Smooth over gaps by interpolating from valid neighbors
  for (let i = 1; i < positions.length; i++) {
    if (isGap[i]) {
      // Find valid neighbors before and after the gap
      let beforeIdx = i - 1;
      let afterIdx = i + 1;

      // Walk back to find valid sample
      while (beforeIdx > 0 && isGap[beforeIdx]) beforeIdx--;
      // Walk forward to find valid sample
      while (afterIdx < positions.length - 1 && isGap[afterIdx]) afterIdx++;

      // Interpolate velocity at gap
      if (beforeIdx >= 0 && afterIdx < positions.length && !isGap[beforeIdx] && !isGap[afterIdx]) {
        // Linear interpolation between valid neighbors
        const t = (times[i] - times[beforeIdx]) / (times[afterIdx] - times[beforeIdx]);
        rawVel[i] = rawVel[beforeIdx] + t * (rawVel[afterIdx] - rawVel[beforeIdx]);
      } else if (beforeIdx >= 0 && !isGap[beforeIdx]) {
        // Only have before - use it
        rawVel[i] = rawVel[beforeIdx];
      } else if (afterIdx < positions.length && !isGap[afterIdx]) {
        // Only have after - use it
        rawVel[i] = rawVel[afterIdx];
      }
      // else keep the gap velocity as calculated
    }
  }

  // Debug raw velocity stats
  const rawRange = getFiniteMinMax(rawVel);
  if (rawRange) {
    debugLog('Raw velocity range: ' + rawRange.min.toFixed(0) + ' to ' + rawRange.max.toFixed(0) + ' mm/s');
  }

  if (filterType === 'none') {
    // Even "no filter" should clamp extreme outliers
    return rawVel.map(v => Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v)));
  }

  // Step 2: Apply median filter to remove spikes (very effective)
  const medianWin = Math.min(7, win);
  let filtered = medianFilter(rawVel, medianWin);

  // Step 3: Clamp extreme values before smoothing
  filtered = filtered.map(v => Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v)));

  // Step 4: Apply smoothing
  if (filterType === 'moving_average') {
    filtered = movingAverageFilter(filtered, win);
  } else {
    // Savitzky-Golay smoothing (not derivative)
    filtered = savitzkyGolayFilter(filtered, win, 3, 0, 1);
  }

  // Debug filtered velocity stats
  const filtRange = getFiniteMinMax(filtered);
  if (filtRange) {
    debugLog('Filtered velocity range: ' + filtRange.min.toFixed(0) + ' to ' + filtRange.max.toFixed(0) + ' mm/s');
  }

  return filtered;
}

// Get current filter status for display
function getFilterStatus() {
  const params = getParams();
  const filterType = params.filterType || 'savgol';

  return {
    active: filterType !== 'none',
    type: filterType === 'savgol' ? 'Savitzky-Golay' :
          filterType === 'moving_average' ? 'Moving Average' : 'None',
    posWindow: params.positionFilterWindow || 5,
    velWindow: params.velocityFilterWindow || 11
  };
}

function convertADCtoMM(adc, type, params) {
  if (type === 'front') {
    // Front: direct linear conversion from ADC to wheel travel (mm)
    // ADC range 0-4095 maps to 0-frontPotStroke mm
    return adc * (params.frontPotStroke / 4095);
  } else {
    // Rear: two-step conversion
    // Step 1: ADC to shock stroke (mm)
    const shockMM = adc * (params.rearPotStroke / 4095);
    // Step 2: Shock stroke to wheel travel via cubic polynomial
    // wheel_mm = a + b*shock + c*shock^2 + d*shock^3
    return params.rearA +
           params.rearB * shockMM +
           params.rearC * shockMM * shockMM +
           params.rearD * shockMM * shockMM * shockMM;
  }
}

function processData(params) {
  clearDebug();
  debugLog('=== processData called ===');
  debugLog('rawData length: ' + rawData.length);

  if (rawData.length === 0) {
    debugLog('ERROR: No data loaded!');
    return [];
  }

  // Find column names (handle different formats)
  const sample = rawData[0];
  debugLog('First row keys: ' + Object.keys(sample).join(', '));
  debugLog('First row time value: ' + sample.time + ' (type: ' + typeof sample.time + ')');
  const cols = Object.keys(sample);
  debugLog('CSV columns found: ' + cols.length + ' columns');

  // Flexible column detection
  const findCol = (options) => {
    for (const opt of options) {
      if (sample.hasOwnProperty(opt)) return opt;
    }
    return null;
  };

  const frontCol = findCol(['front_disp', 'front', 'Front', 'FRONT', 'front_mm']);
  const rearCol = findCol(['rear_disp', 'rear', 'Rear', 'REAR', 'rear_mm']);
  let timeCol = findCol(['time', 'Time', 'TIME', 't', 'timestamp', 'seconds']);
  const microsCol = findCol(['micros', 'Micros', 'microseconds']);

  // If time column has issues, try to use micros column
  let useMicrosAsTime = false;

  // Check for missing columns
  const missing = [];
  if (!frontCol) missing.push('front_disp or front');
  if (!rearCol) missing.push('rear_disp or rear');
  if (!timeCol) missing.push('time');

  if (missing.length > 0) {
    debugLog('ERROR: Missing columns: ' + missing.join(', '));
    debugLog('Available columns: ' + cols.join(', '));
    processedData = [];
    return processedData;
  }

  debugLog('Using columns - time: ' + timeCol + ', front: ' + frontCol + ', rear: ' + rearCol);

  // Check if time column data looks valid
  const timeTest = rawData.slice(0, 10).filter(d => {
    const t = d[timeCol];
    return t !== undefined && t !== null && !isNaN(Number(t));
  }).length;

  debugLog('Time column validity: ' + timeTest + '/10 rows have valid time');

  // If time column is mostly invalid but we have micros, use that instead
  if (timeTest < 5 && microsCol) {
    debugLog('Switching to micros column as time source');
    useMicrosAsTime = true;
  }

  // Get time value from row, using appropriate column
  const getTime = (d) => {
    if (useMicrosAsTime && microsCol) {
      // Convert micros to seconds, relative to first sample
      return Number(d[microsCol]) / 1000000;
    }
    return Number(d[timeCol]);
  };

  // Calculate time offset (for micros, we need to subtract the first timestamp)
  let timeOffset = 0;
  if (useMicrosAsTime && rawData.length > 0) {
    timeOffset = getTime(rawData[0]);
  }

  // Show sample time values
  const sampleTimes = rawData.slice(0, 3).map(d => getTime(d) - timeOffset);
  const lastSampleTimes = rawData.slice(-3).map(d => getTime(d) - timeOffset);
  debugLog('First 3 time values: ' + sampleTimes.map(t => t.toFixed(4)).join(', '));
  debugLog('Last 3 time values: ' + lastSampleTimes.map(t => t.toFixed(4)).join(', '));
  debugLog('Trim settings: start=' + params.trimStart + ', end=' + (params.trimEnd === Infinity ? 'Infinity' : params.trimEnd));

  // Filter by time - very permissive, just check for valid numbers
  let rejectedByNaN = 0, rejectedByInfinite = 0, rejectedByTrimStart = 0, rejectedByTrimEnd = 0;

  let filtered = rawData.filter(d => {
    const t = getTime(d) - timeOffset;
    if (isNaN(t)) { rejectedByNaN++; return false; }
    if (!isFinite(t)) { rejectedByInfinite++; return false; }
    if (t < params.trimStart) { rejectedByTrimStart++; return false; }
    if (t > params.trimEnd) { rejectedByTrimEnd++; return false; }
    return true;
  });

  debugLog('Time filter: ' + rawData.length + ' -> ' + filtered.length + ' samples');
  if (rejectedByNaN + rejectedByInfinite + rejectedByTrimStart + rejectedByTrimEnd > 0) {
    debugLog('  Rejected: NaN=' + rejectedByNaN + ', Infinite=' + rejectedByInfinite +
             ', <trimStart=' + rejectedByTrimStart + ', >trimEnd=' + rejectedByTrimEnd);
  }

  // FALLBACK: If filtering failed, use all data with index-based time
  let useFallbackTime = false;
  if (filtered.length < 10 && rawData.length > 10) {
    debugLog('WARNING: Filter failed! Analyzing first row...');
    const d = rawData[0];
    const t = getTime(d) - timeOffset;
    debugLog('  Row 0 time raw: ' + d[timeCol]);
    debugLog('  Row 0 time computed: ' + t);
    debugLog('  isNaN: ' + isNaN(t) + ', isFinite: ' + isFinite(t));
    debugLog('  >= trimStart: ' + (t >= params.trimStart));
    debugLog('  <= trimEnd: ' + (t <= params.trimEnd));

    debugLog('Using FALLBACK: index-based time at 1000Hz');
    filtered = rawData.slice(); // Make a copy
    useFallbackTime = true;
  }

  // Filter settings from params (matching Python analysis)
  const MEDIAN_FILTER_SIZE = params.medianFilterSize || 5;
  const MAX_POSITION_CHANGE = params.maxPosChange || 15.0; // mm per sample
  const VELOCITY_WINDOW = params.velocityWindow || 21;
  const VELOCITY_POLYORDER = 3;
  const sampleRate = 1000; // Hz

  // Step 1: Extract raw position arrays
  debugLog('Extracting positions...');
  const rawFront = [];
  const rawRear = [];
  const times = [];

  // Track raw ADC values for debugging
  let frontADCMin = Infinity, frontADCMax = -Infinity;
  let rearADCMin = Infinity, rearADCMax = -Infinity;

  for (let i = 0; i < filtered.length; i++) {
    const d = filtered[i];

    // Track raw ADC range
    const frontADC = Number(d[frontCol]);
    const rearADC = Number(d[rearCol]);
    if (!isNaN(frontADC)) {
      frontADCMin = Math.min(frontADCMin, frontADC);
      frontADCMax = Math.max(frontADCMax, frontADC);
    }
    if (!isNaN(rearADC)) {
      rearADCMin = Math.min(rearADCMin, rearADC);
      rearADCMax = Math.max(rearADCMax, rearADC);
    }

    // Invert ADC BEFORE conversion (if pot reads backwards)
    let frontADCVal = Number(d[frontCol]);
    let rearADCVal = Number(d[rearCol]);

    if (params.invertFront) frontADCVal = 4095 - frontADCVal;
    if (params.invertRear) rearADCVal = 4095 - rearADCVal;

    let frontMM = convertADCtoMM(frontADCVal, 'front', params);
    let rearMM = convertADCtoMM(rearADCVal, 'rear', params);

    rawFront.push(frontMM);
    rawRear.push(rearMM);

    // Calculate time
    if (useFallbackTime) {
      times.push(i / sampleRate);
    } else {
      times.push(getTime(d) - timeOffset);
    }
  }

  debugLog('Raw positions extracted: ' + rawFront.length + ' samples');
  debugLog('=== RAW ADC VALUES ===');
  debugLog('Front ADC range: ' + frontADCMin + ' to ' + frontADCMax + ' (expected 0-4095)');
  debugLog('Rear ADC range: ' + rearADCMin + ' to ' + rearADCMax + ' (expected 0-4095)');

  // Calculate what pot stroke these ADC values imply
  const frontPotUsed = (frontADCMax - frontADCMin) / 4095 * params.frontPotStroke;
  const rearPotUsed = (rearADCMax - rearADCMin) / 4095 * params.rearPotStroke;
  debugLog('Front pot stroke used: ' + frontPotUsed.toFixed(1) + 'mm of ' + params.frontPotStroke + 'mm');
  debugLog('Rear pot stroke used: ' + rearPotUsed.toFixed(1) + 'mm of ' + params.rearPotStroke + 'mm');
  
  // Debug: Check time data quality
  if (times.length > 0) {
    const timeRangeStats = getFiniteMinMax(times);
    if (timeRangeStats) {
      const { min: timeMin, max: timeMax } = timeRangeStats;
      const timeRange = timeMax - timeMin;
      const avgDt = timeRange / (times.length - 1);

      // Check for backwards time
      let backwardsCount = 0;
      for (let i = 1; i < times.length; i++) {
        if (times[i] < times[i - 1]) backwardsCount++;
      }

      debugLog('Time range: ' + timeMin.toFixed(4) + 's to ' + timeMax.toFixed(4) + 's (duration: ' + timeRange.toFixed(2) + 's)');
      debugLog('Average dt: ' + (avgDt * 1000).toFixed(2) + 'ms (' + (1 / avgDt).toFixed(0) + ' Hz)');

      if (backwardsCount > 0) {
        debugLog('[!] WARNING: Time goes backwards ' + backwardsCount + ' times! Data needs sorting.');
      }
    }
  }

  // Step 2: SORT AND CLEAN DATA FIRST (before velocity calculation!)
  // Combine into objects for sorting
  debugLog('Sorting and cleaning data by time...');
  let dataPoints = [];
  for (let i = 0; i < rawFront.length; i++) {
    dataPoints.push({
      time: times[i],
      rawFront: rawFront[i],
      rawRear: rawRear[i]
    });
  }

  // Sort by time
  dataPoints.sort((a, b) => a.time - b.time);

  // Remove ONLY strictly backwards time (keep all forward/equal timestamps)
  // This is more permissive to avoid losing data
  let timeIssues = 0;
  let duplicates = 0;
  const cleanedPoints = [];
  let lastTime = -Infinity;
  let firstRemovedTime = null;
  let lastRemovedTime = null;

  // MUCH more permissive: only skip if time goes BACKWARDS
  // Keep duplicates and very close timestamps - they're valid data!
  const DUPLICATE_THRESHOLD = 0.00001; // 10 microseconds - only true duplicates

  for (let i = 0; i < dataPoints.length; i++) {
    const pt = dataPoints[i];
    // Only skip if time is strictly less than previous (backwards)
    if (pt.time < lastTime - DUPLICATE_THRESHOLD) {
      timeIssues++;
      if (!firstRemovedTime) firstRemovedTime = pt.time;
      lastRemovedTime = pt.time;
      continue;
    }
    // Only skip true duplicates (exactly same timestamp)
    if (Math.abs(pt.time - lastTime) < DUPLICATE_THRESHOLD && cleanedPoints.length > 0) {
      duplicates++;
      if (!firstRemovedTime) firstRemovedTime = pt.time;
      lastRemovedTime = pt.time;
      continue;
    }
    cleanedPoints.push(pt);
    lastTime = pt.time;
  }

  debugLog('=== DATA PIPELINE VERIFICATION ===');
  debugLog('Step 1: Raw CSV rows: ' + rawData.length);
  debugLog('Step 2: After time trim: ' + filtered.length);
  debugLog('Step 3: After extraction: ' + dataPoints.length);
  debugLog('Step 4: After cleaning: ' + cleanedPoints.length);

  if (timeIssues > 0 || duplicates > 0) {
    debugLog('  Removed: ' + timeIssues + ' backwards, ' + duplicates + ' duplicates');
    if (firstRemovedTime !== null) {
      debugLog('  Removed points time range: ' + firstRemovedTime.toFixed(4) + 's to ' + lastRemovedTime.toFixed(4) + 's');
    }
  }

  // Verify no major data loss
  const lossPercent = ((rawData.length - cleanedPoints.length) / rawData.length * 100).toFixed(1);
  if (cleanedPoints.length < rawData.length * 0.9) {
    debugLog('[WARNING] Significant data loss: ' + lossPercent + '% of points removed!');
  }

  // Extract clean arrays
  const cleanTimes = cleanedPoints.map(p => p.time);
  const cleanFront = cleanedPoints.map(p => p.rawFront);
  const cleanRear = cleanedPoints.map(p => p.rawRear);

  debugLog('After cleaning: ' + cleanedPoints.length + ' samples (removed ' + (dataPoints.length - cleanedPoints.length) + ')');

  // Step 3: Filter positions on CLEAN data (gap-aware to prevent cross-gap smoothing)
  debugLog('Applying position filters (median=' + MEDIAN_FILTER_SIZE + ', maxChange=' + MAX_POSITION_CHANGE + ', gap-aware)...');

  // Median filter with gap awareness
  const medianFn = (arr, win) => medianFilter(arr, win);
  let filteredFront = applyFilterWithGaps(cleanFront, cleanTimes, medianFn, MEDIAN_FILTER_SIZE);
  let filteredRear = applyFilterWithGaps(cleanRear, cleanTimes, medianFn, MEDIAN_FILTER_SIZE);

  // Position change limiting (already gap-aware by nature - operates sample by sample)
  filteredFront = limitPositionChange(filteredFront, MAX_POSITION_CHANGE);
  filteredRear = limitPositionChange(filteredRear, MAX_POSITION_CHANGE);

  // Apply user-selected filter WITH GAP AWARENESS
  // This prevents artificial smoothing across time discontinuities
  const posFilterWindow = params.positionFilterWindow || 5;
  debugLog('Applying user filter: ' + params.filterType + ' (window=' + posFilterWindow + ', gap-aware)');

  // Create filter function based on user selection
  const posFilterFn = (arr, win) => {
    if (params.filterType === 'moving_average') return movingAverageFilter(arr, win);
    if (params.filterType === 'none') return arr.slice();
    return savitzkyGolayFilter(arr, win, 3, 0, 1); // Default: Savitzky-Golay
  };

  filteredFront = applyFilterWithGaps(filteredFront, cleanTimes, posFilterFn, posFilterWindow);
  filteredRear = applyFilterWithGaps(filteredRear, cleanTimes, posFilterFn, posFilterWindow);

  // Step 3b: Auto-calibrate baseline using robust percentile method
  // This finds a consistent minimum, ignoring brief outlier dips
  const frontBaseline = findRobustBaseline(filteredFront);
  const rearBaseline = findRobustBaseline(filteredRear);
  debugLog('Auto-calibration: Front baseline=' + frontBaseline.toFixed(2) + 'mm, Rear baseline=' + rearBaseline.toFixed(2) + 'mm');

  // Shift data so consistent minimum is at 0
  filteredFront = filteredFront.map(v => v - frontBaseline);
  filteredRear = filteredRear.map(v => v - rearBaseline);
  debugLog('Data shifted to zero baseline');

  // Step 4: Calculate velocities on CLEAN, SORTED data
  const velFilterWindow = params.velocityFilterWindow || 11;
  debugLog('Calculating filtered velocities (window=' + velFilterWindow + ')...');
  const frontVelocities = calculateFilteredVelocity(filteredFront, cleanTimes, velFilterWindow);
  const rearVelocities = calculateFilteredVelocity(filteredRear, cleanTimes, velFilterWindow);

  // Debug velocity range
  const frontVelRange = getFiniteMinMax(frontVelocities);
  const rearVelRange = getFiniteMinMax(rearVelocities);
  if (frontVelRange) {
    debugLog('Front velocity range: ' + frontVelRange.min.toFixed(0) + ' to ' + frontVelRange.max.toFixed(0) + ' mm/s');
  }
  if (rearVelRange) {
    debugLog('Rear velocity range: ' + rearVelRange.min.toFixed(0) + ' to ' + rearVelRange.max.toFixed(0) + ' mm/s');
  }

  // Step 5: Build final processed data array
  processedData = [];
  for (let i = 0; i < cleanedPoints.length; i++) {
    processedData.push({
      time: cleanTimes[i],
      front: filteredFront[i],
      rear: filteredRear[i],
      front_vel: frontVelocities[i],
      rear_vel: rearVelocities[i],
      index: i
    });
  }

  debugLog('Step 5: Filtered arrays - front: ' + filteredFront.length + ', rear: ' + filteredRear.length);
  debugLog('Step 6: Velocity arrays - front: ' + frontVelocities.length + ', rear: ' + rearVelocities.length);
  debugLog('Step 7: Final processedData: ' + processedData.length + ' samples');

  // Final verification
  if (processedData.length !== cleanedPoints.length) {
    debugLog('[ERROR] Data mismatch! cleanedPoints=' + cleanedPoints.length + ' but processedData=' + processedData.length);
  }

  if (processedData.length > 0) {
    debugLog('Time range: ' + processedData[0].time.toFixed(4) + 's to ' + processedData[processedData.length-1].time.toFixed(4) + 's');
    debugLog('First: front=' + processedData[0].front.toFixed(1) + 'mm, rear=' + processedData[0].rear.toFixed(1) + 'mm');

    // Check for data around specific time range (user's problem area)
    const around429 = processedData.filter(d => d.time >= 4.28 && d.time <= 4.41);
    debugLog('Points in range 4.28-4.41s: ' + around429.length);

    // Log samples in problem range for debugging
    if (around429.length > 0) {
      debugLog('=== SAMPLE DATA IN PROBLEM RANGE ===');
      for (let j = 0; j < Math.min(5, around429.length); j++) {
        const s = around429[j];
        debugLog('  t=' + s.time.toFixed(4) + ' front=' + s.front.toFixed(1) + 'mm fvel=' + s.front_vel.toFixed(0) +
                 ' rear=' + s.rear.toFixed(1) + 'mm rvel=' + s.rear_vel.toFixed(0));
      }
    } else {
      debugLog('[WARNING] NO DATA in 4.28-4.41s range!');
    }

    // Also check raw data to compare
    debugLog('=== RAW DATA CHECK at t~4.35 ===');
    const rawAround435 = rawData.filter(d => {
      const t = Number(d.time);
      return t >= 4.34 && t <= 4.36;
    });
    debugLog('Raw rows at t=4.34-4.36: ' + rawAround435.length);
    if (rawAround435.length > 0) {
      for (let j = 0; j < Math.min(3, rawAround435.length); j++) {
        const r = rawAround435[j];
        debugLog('  RAW: t=' + r.time + ' front_disp=' + r.front_disp + ' rear_disp=' + r.rear_disp);
      }
    } else {
      debugLog('[WARNING] NO RAW DATA at t=4.34-4.36!');
    }
  }
  return processedData;
}

// ==================== IMPACT DETECTION ====================
function detectImpacts(data, velKey, posKey, params) {
  const impacts = [];
  const velThresh = params.velocityThreshold;
  let state = 'searching';
  let currentImpact = {};

  for (let i = 1; i < data.length; i++) {
    const vel = data[i][velKey];
    const pos = data[i][posKey];
    const prevVel = data[i - 1][velKey];

    if (state === 'searching') {
      // Look for compression start (velocity goes positive)
      if (prevVel <= velThresh && vel > velThresh) {
        state = 'compressing';
        currentImpact = {
          start_index: i,
          start_position: data[i - 1][posKey],
          start_time: data[i - 1].time,
          peak_position: pos
        };
      }
    } else if (state === 'compressing') {
      // Track peak
      if (pos > currentImpact.peak_position) {
        currentImpact.peak_position = pos;
        currentImpact.peak_index = i;
      }

      // Look for rebound start (velocity goes negative)
      if (prevVel >= -velThresh && vel < -velThresh) {
        state = 'rebounding';
        currentImpact.rebound_achieved = true;
      }
    } else if (state === 'rebounding') {
      // Look for next compression or settling
      if (prevVel <= velThresh && vel > velThresh) {
        // New impact starting - finalize current
        currentImpact.end_index = i - 1;
        currentImpact.end_position = data[i - 1][posKey];
        currentImpact.end_time = data[i - 1].time;

        if (currentImpact.rebound_achieved) {
          impacts.push({ ...currentImpact });
        }

        // Start new impact
        state = 'compressing';
        currentImpact = {
          start_index: i,
          start_position: data[i - 1][posKey],
          start_time: data[i - 1].time,
          peak_position: pos
        };
      }
    }
  }

  // Finalize last impact if rebounding
  if (state === 'rebounding' && currentImpact.rebound_achieved) {
    currentImpact.end_index = data.length - 1;
    currentImpact.end_position = data[data.length - 1][posKey];
    currentImpact.end_time = data[data.length - 1].time;
    impacts.push(currentImpact);
  }

  return impacts;
}

// ==================== PACKING DETECTION ====================
function detectPackingEvents(impacts, params, totalTravel) {
  const baseline = totalTravel * (params.sagPercent / 100);
  const packingEvents = [];
  let packedSequence = [];

  for (let i = 0; i < impacts.length; i++) {
    const impact = impacts[i];
    const startPos = impact.start_position;

    // Rule 1: Skip if starting from extended position (drop/jump)
    if (startPos < (baseline - params.extensionThreshold)) {
      if (packedSequence.length >= params.minPackedHits) {
        packingEvents.push(finalizePackingEvent(packedSequence, params, baseline, totalTravel));
      }
      packedSequence = [];
      continue;
    }

    // Rule 2: Check if this hit is packed
    if (startPos > (baseline + params.packedThreshold)) {
      packedSequence.push(impact);

      // Rule 3: Detect phase
      if (packedSequence.length >= 2) {
        const prevStart = packedSequence[packedSequence.length - 2].start_position;
        const delta = startPos - prevStart;

        if (delta > params.ratchetDelta) {
          impact.phase = 'Progressive Ratcheting';
        } else if (Math.abs(delta) < params.stuckThreshold) {
          impact.phase = 'Stuck Deep';
        } else {
          impact.phase = 'Mixed';
        }
      } else {
        impact.phase = 'Initial';
      }
    } else {
      // Rule 4: Hit recovered to normal
      if (packedSequence.length >= params.minPackedHits) {
        packingEvents.push(finalizePackingEvent(packedSequence, params, baseline, totalTravel));
      }
      packedSequence = [];
    }
  }

  // Handle packing at end of data
  if (packedSequence.length >= params.minPackedHits) {
    packingEvents.push(finalizePackingEvent(packedSequence, params, baseline, totalTravel));
  }

  return packingEvents;
}

function finalizePackingEvent(sequence, params, baseline, totalTravel) {
  const startPositions = sequence.map(imp => imp.start_position);
  const peakPositions = sequence.map(imp => imp.peak_position);

  const severity = calculateSeverity(sequence, params, baseline, totalTravel);

  return {
    hit_count: sequence.length,
    start_time: sequence[0].start_time,
    end_time: sequence[sequence.length - 1].end_time,
    duration: sequence[sequence.length - 1].end_time - sequence[0].start_time,
    baseline_used: baseline,
    max_depth: getFiniteMax(peakPositions) ?? 0,
    max_start_depth: getFiniteMax(startPositions) ?? 0,
    depth_above_baseline: (getFiniteMax(startPositions) ?? baseline) - baseline,
    phases_present: [...new Set(sequence.map(imp => imp.phase || 'Initial'))],
    impacts: sequence,
    severity: severity,
    category: getSeverityCategory(severity)
  };
}

function calculateSeverity(sequence, params, baseline, totalTravel) {
  let score = 0;

  // Component 1: Number of hits (3 = +10, 4 = +20, etc.)
  score += (sequence.length - 2) * 10;

  // Component 2: Depth above baseline
  const startPositions = sequence.map(imp => imp.start_position);
  const maxDepth = getFiniteMax(startPositions) ?? baseline;
  const depthAboveBaseline = maxDepth - baseline;
  score += depthAboveBaseline * 0.5;

  // Component 3: Stuck phase bonus
  if (sequence.some(imp => imp.phase === 'Stuck Deep')) {
    score += 20;
  }

  // Component 4: Very deep in travel penalty
  const maxPeak = getFiniteMax(sequence.map((imp) => imp.peak_position)) ?? 0;
  if (maxPeak > totalTravel * 0.70) {
    score += 15;
  }

  return Math.min(Math.round(score), 100);
}

function getSeverityCategory(score) {
  if (score <= 25) return 'Mild';
  if (score <= 50) return 'Moderate';
  if (score <= 75) return 'Severe';
  return 'Critical';
}

// ==================== OVERALL RATING ====================
function calculatePackingIndex(impacts, packingEvents, totalRunTime, params) {
  const totalImpacts = impacts.length;
  if (totalImpacts === 0) return 0;

  const packedImpacts = packingEvents.reduce((sum, evt) => sum + evt.hit_count, 0);
  const totalPackedTime = packingEvents.reduce((sum, evt) => sum + evt.duration, 0);

  // Frequency component (40% weight)
  const frequencyScore = (packedImpacts / totalImpacts) * 40;

  // Severity component (40% weight)
  const avgEventSeverity = packingEvents.length > 0
    ? packingEvents.reduce((sum, evt) => sum + evt.severity, 0) / packingEvents.length
    : 0;
  const severityScore = (avgEventSeverity / 100) * 40;

  // Duration component (20% weight)
  const durationScore = totalRunTime > 0 ? (totalPackedTime / totalRunTime) * 20 : 0;

  return Math.min(Math.round(frequencyScore + severityScore + durationScore), 100);
}

function getOverallRating(packingIndex) {
  if (packingIndex <= 15) return 'Excellent';
  if (packingIndex <= 30) return 'Good';
  if (packingIndex <= 50) return 'Moderate';
  if (packingIndex <= 75) return 'Poor';
  return 'Critical';
}

// ==================== STATISTICS ====================
function calculateStats(data, key) {
  const values = data.map(d => d[key]).filter(v => !isNaN(v));
  if (values.length === 0) return null;

  // Calculate total movement (sum of absolute position changes)
  let totalMovement = 0;
  for (let i = 1; i < values.length; i++) {
    totalMovement += Math.abs(values[i] - values[i - 1]);
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const n = sortedValues.length;
  const sum = sortedValues.reduce((a, b) => a + b, 0);
  const mean = sum / n;
  const variance = sortedValues.reduce((a, v) => a + (v - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance);
  const p95Index = Math.floor(n * 0.95);

  return {
    min: sortedValues[0],
    max: sortedValues[n - 1],
    mean: mean,
    std: std,
    p95: sortedValues[p95Index],
    range: sortedValues[n - 1] - sortedValues[0],
    totalMovement: totalMovement
  };
}

// Detect individual strokes and calculate per-stroke metrics
// A stroke is a compression (vel > 0) or rebound (vel < 0) phase between zero-crossings
function detectStrokes(data, posKey, velKey, params, totalTravel) {
  const strokes = { compression: [], rebound: [] };
  if (data.length < 3) return strokes;

  const minDepth = params?.minStrokeDepth || 5;
  const minSamples = params?.minStrokeSamples || 5;
  const bottomOutPct = params?.bottomOutThreshold || 95;
  const topOutMm = params?.topOutThreshold || 5;

  let strokeStart = null;
  let strokeType = null; // 'compression' or 'rebound'
  let maxVelIdx = null;
  let maxVelAbs = 0;

  for (let i = 1; i < data.length; i++) {
    const vel = data[i][velKey];
    const prevVel = data[i - 1][velKey];

    // Detect zero-crossing: start of new stroke
    const crossingToComp = prevVel <= 0 && vel > 0;
    const crossingToReb = prevVel >= 0 && vel < 0;

    // If we're in a stroke and hit a zero-crossing, end the current stroke
    if (strokeStart !== null && (crossingToComp || crossingToReb)) {
      // Zenith is at index i-1 (where velocity was ~0 before crossing)
      const zenithIdx = i - 1;
      const strokeSamples = zenithIdx - strokeStart;

      // Only count if meets minimum time requirement
      if (strokeSamples >= minSamples) {
        const stroke = buildStroke(data, strokeStart, zenithIdx, maxVelIdx, posKey, velKey, strokeType, totalTravel, bottomOutPct, topOutMm, params);
        // Only count if meets minimum depth requirement
        if (stroke && stroke.depth >= minDepth) {
          strokes[strokeType].push(stroke);
        }
      }
      strokeStart = null;
    }

    // Start new stroke on zero-crossing
    if (crossingToComp) {
      strokeStart = i;
      strokeType = 'compression';
      maxVelIdx = i;
      maxVelAbs = Math.abs(vel);
    } else if (crossingToReb) {
      strokeStart = i;
      strokeType = 'rebound';
      maxVelIdx = i;
      maxVelAbs = Math.abs(vel);
    }

    // Track max velocity within current stroke
    if (strokeStart !== null && Math.abs(vel) > maxVelAbs) {
      maxVelAbs = Math.abs(vel);
      maxVelIdx = i;
    }
  }

  return strokes;
}

// Build a stroke object with all metrics
function buildStroke(data, startIdx, zenithIdx, maxVelIdx, posKey, velKey, type, totalTravel, bottomOutPct, topOutMm, params) {
  if (startIdx >= zenithIdx || maxVelIdx < startIdx || maxVelIdx > zenithIdx) return null;

  const startPos = data[startIdx][posKey];
  const zenithPos = data[zenithIdx][posKey];
  const maxVelPos = data[maxVelIdx][posKey];
  const maxVel = Math.abs(data[maxVelIdx][velKey]);

  const velMagnitudes = [];
  for (let i = startIdx; i <= zenithIdx; i++) {
    const v = data[i][velKey];
    if (Number.isFinite(v)) velMagnitudes.push(Math.abs(v));
  }
  let p95Velocity = maxVel;
  if (velMagnitudes.length > 0) {
    velMagnitudes.sort((a, b) => a - b);
    const p95Ix = Math.floor(velMagnitudes.length * 0.95);
    p95Velocity = velMagnitudes[p95Ix] ?? velMagnitudes[velMagnitudes.length - 1];
  }

  const depth = type === 'compression' ? (zenithPos - startPos) : (startPos - zenithPos);

  // Bottom-out: zenith position reaches X% of total travel (for compression)
  // Top-out: zenith position is within Y mm of 0 (for rebound)
  const bottomOutThresholdMm = totalTravel * (bottomOutPct / 100);
  const bottomedOut = type === 'compression' && zenithPos >= bottomOutThresholdMm;
  const toppedOut = type === 'rebound' && zenithPos <= topOutMm;

  // For rear strokes, calculate shock velocity (pot speed before linkage conversion)
  let shockVelocity = null;
  if (posKey === 'rearMM' && params) {
    // Reverse the linkage conversion: wheel position → shock position
    const shockPosAtMaxVel = reverseLinkageConversion(maxVelPos, params);
    // Convert wheel velocity to shock velocity using linkage derivative
    shockVelocity = wheelVelToShockVel(maxVel, shockPosAtMaxVel, params);
  }

  return {
    type: type,
    startTime: data[startIdx].time,
    zenithTime: data[zenithIdx].time,
    duration: data[zenithIdx].time - data[startIdx].time,
    startPos: startPos,
    zenithPos: zenithPos,
    depth: depth,
    maxVelocity: maxVel,
    p95Velocity: p95Velocity,
    shockVelocity: shockVelocity,  // null for front, shock vel for rear
    maxVelTime: data[maxVelIdx].time,
    maxVelPos: maxVelPos,
    // Position within stroke where max velocity occurred (0-100%)
    maxVelPosPercent: depth > 0 ? ((maxVelPos - startPos) / depth * 100) : 0,
    // Acceleration zone: start to max velocity
    accelDuration: data[maxVelIdx].time - data[startIdx].time,
    // Deceleration zone: max velocity to zenith
    decelDuration: data[zenithIdx].time - data[maxVelIdx].time,
    // Bottom-out and top-out flags
    bottomedOut: bottomedOut,
    toppedOut: toppedOut
  };
}

// Calculate aggregate stats from detected strokes
function calculateStrokeStats(strokes) {
  const calcStats = (arr) => {
    if (arr.length === 0) return { mean: 0, max: 0, p95: 0, count: 0 };
    const sorted = [...arr].sort((a, b) => a - b);
    const maxVal = getFiniteMax(arr);
    return {
      mean: arr.reduce((a, b) => a + b, 0) / arr.length,
      max: maxVal ?? 0,
      p95: sorted[Math.floor(arr.length * 0.95)] || sorted[sorted.length - 1],
      count: arr.length
    };
  };

  const compStrokes = strokes.compression;
  const rebStrokes = strokes.rebound;

  // Count bottom-outs (compression strokes that hit near max travel)
  const bottomOutCount = compStrokes.filter(s => s.bottomedOut).length;
  // Count top-outs (rebound strokes that hit near 0)
  const topOutCount = rebStrokes.filter(s => s.toppedOut).length;

  return {
    compression: {
      ...calcStats(compStrokes.map(s => s.maxVelocity)),
      depthMean: compStrokes.length > 0 ? compStrokes.reduce((a, s) => a + s.depth, 0) / compStrokes.length : 0,
      depthMax: compStrokes.length > 0 ? (getFiniteMax(compStrokes.map((s) => s.depth)) ?? 0) : 0,
      bottomOutCount: bottomOutCount
    },
    rebound: {
      ...calcStats(rebStrokes.map(s => s.maxVelocity)),
      depthMean: rebStrokes.length > 0 ? rebStrokes.reduce((a, s) => a + s.depth, 0) / rebStrokes.length : 0,
      depthMax: rebStrokes.length > 0 ? (getFiniteMax(rebStrokes.map((s) => s.depth)) ?? 0) : 0,
      topOutCount: topOutCount
    },
    allStrokes: { compression: compStrokes, rebound: rebStrokes }
  };
}

// Legacy function for compatibility - now uses stroke-based analysis
function calculateVelocityStats(data, velKey, posKey, params, totalTravel) {
  const strokes = detectStrokes(data, posKey, velKey, params, totalTravel);
  const stats = calculateStrokeStats(strokes);
  return stats;
}

// Reverse linkage conversion: wheel position → shock position
// Solves: wheel = a + b*shock + c*shock² + d*shock³ for shock
function reverseLinkageConversion(wheelPos, params) {
  const a = params.rearA;
  const b = params.rearB;
  const c = params.rearC;
  const d = params.rearD;

  // For linear linkage (c=0, d=0): shock = (wheel - a) / b
  if (c === 0 && d === 0) {
    return (wheelPos - a) / b;
  }

  // For non-linear linkage, use iterative Newton-Raphson method
  // Initial guess: linear approximation
  let shock = (wheelPos - a) / b;

  // Newton-Raphson iterations (typically converges in 3-5 iterations)
  for (let i = 0; i < 10; i++) {
    const f = a + b*shock + c*shock*shock + d*shock*shock*shock - wheelPos;
    const fPrime = b + 2*c*shock + 3*d*shock*shock;

    if (Math.abs(fPrime) < 1e-10) break; // Avoid division by zero

    const delta = f / fPrime;
    shock = shock - delta;

    if (Math.abs(delta) < 1e-6) break; // Converged
  }

  return shock;
}

// Calculate linkage ratio at a given shock position
// Returns d(wheel)/d(shock) based on polynomial coefficients
function getLinkageRatio(shockPos, params) {
  // linkage_ratio = b + 2c*shock + 3d*shock²
  return params.rearB +
         2 * params.rearC * shockPos +
         3 * params.rearD * shockPos * shockPos;
}

// Convert wheel velocity to shock velocity
function wheelVelToShockVel(wheelVel, shockPos, params) {
  const linkageRatio = getLinkageRatio(shockPos, params);
  if (linkageRatio === 0) return wheelVel; // Avoid division by zero
  return wheelVel / linkageRatio;
}


// ==================== MAIN ANALYSIS ====================
function runAnalysis() {
  analysisResults = null;
  if (rawData.length === 0) {
    return;
  }
  const params = getParams();
  processData(params);
  if (processedData.length < 10) {
    analysisResults = null;
    return;
  }
  impacts.front = detectImpacts(processedData, 'front_vel', 'front', params);
  impacts.rear = detectImpacts(processedData, 'rear_vel', 'rear', params);
  packingEvents.front = detectPackingEvents(impacts.front, params, params.frontTravel);
  packingEvents.rear = detectPackingEvents(impacts.rear, params, params.rearTravel);
  const totalRunTime = processedData[processedData.length - 1].time - processedData[0].time;
  const rearPackingIndex = calculatePackingIndex(impacts.rear, packingEvents.rear, totalRunTime, params);
  const frontPackingIndex = calculatePackingIndex(impacts.front, packingEvents.front, totalRunTime, params);
  const combinedIndex = Math.round((rearPackingIndex + frontPackingIndex) / 2);
  const frontPosStats = calculateStats(processedData, 'front');
  const rearPosStats = calculateStats(processedData, 'rear');
  const frontVelStats = calculateVelocityStats(processedData, 'front_vel', 'front', params, params.frontTravel);
  const rearVelStats = calculateVelocityStats(processedData, 'rear_vel', 'rear', params, params.rearTravel);
  debugLog('=== STROKE DETECTION ===');
  analysisResults = {
    timestamp: Date.now(),
    params,
    packingIndex: combinedIndex,
    frontPackingIndex,
    rearPackingIndex,
    rating: getOverallRating(combinedIndex),
    totalImpacts: { front: impacts.front.length, rear: impacts.rear.length },
    packedImpacts: {
      front: packingEvents.front.reduce((s, e) => s + e.hit_count, 0),
      rear: packingEvents.rear.reduce((s, e) => s + e.hit_count, 0),
    },
    packingEvents: { front: packingEvents.front, rear: packingEvents.rear },
    totalRunTime,
    totalPackedTime: {
      front: packingEvents.front.reduce((s, e) => s + e.duration, 0),
      rear: packingEvents.rear.reduce((s, e) => s + e.duration, 0),
    },
    stats: {
      front: { position: frontPosStats, velocity: frontVelStats },
      rear: { position: rearPosStats, velocity: rearVelStats },
    },
  };
}

/**
 * Map app telemetry (fork/shock/time) to Jack row shape. Normalized 0–1 fork/shock → 0–4095 ADC scale.
 */
export function telemetryToJackRawRows(telemetryData) {
  if (!Array.isArray(telemetryData) || telemetryData.length === 0) return [];
  let hi = 0;
  let hasAny = false;
  for (let i = 0; i < telemetryData.length; i += 1) {
    const row = telemetryData[i];
    const f = pickFinite(row.front_disp, row.fork, row.front, row.fork_normalized);
    const r = pickFinite(row.rear_disp, row.shock, row.rear, row.shock_normalized);
    if (Number.isFinite(f)) {
      if (!hasAny || f > hi) hi = f;
      hasAny = true;
    }
    if (Number.isFinite(r)) {
      if (!hasAny || r > hi) hi = r;
      hasAny = true;
    }
  }
  if (!hasAny) return [];
  const scaleToAdc = hi <= 1.5;

  return telemetryData
    .map((row) => {
    let f = pickFinite(row.front_disp, row.fork, row.front, row.fork_normalized);
    let r = pickFinite(row.rear_disp, row.shock, row.rear, row.shock_normalized);
    if (!Number.isFinite(f) || !Number.isFinite(r)) return null;
    if (scaleToAdc) {
      if (Number.isFinite(f)) f *= 4095;
      if (Number.isFinite(r)) r *= 4095;
    }
    const t = pickFinite(row.time, row.Time, row.t, row.timestamp, row.timestamp_ms);
    if (!Number.isFinite(t)) return null;
    return {
      ...row,
      time: t > 1e8 ? t / 1000 : t,
      front_disp: f,
      rear_disp: r,
    };
  })
    .filter(Boolean);
}

/**
 * Full Jack pipeline: packing index, impacts, stroke stats (matches jack-benchmark.html).
 */
export function runJackPackingAnalysis(telemetryOrRaw, partialParams = {}) {
  _jackRuntimeParams = sanitizeJackParams(partialParams);
  rawData = Array.isArray(telemetryOrRaw) ? [...telemetryOrRaw] : [];
  sortRawDataByTime();
  runAnalysis();
  if (!analysisResults) {
    return {
      ok: false,
      error: rawData.length === 0 ? 'No rows' : 'Not enough samples after processing',
      sampleCount: processedData.length,
      analysisResults: null,
      processedData: [...processedData],
    };
  }
  return {
    ok: true,
    analysisResults: { ...analysisResults },
    processedData: [...processedData],
    impacts: { front: [...impacts.front], rear: [...impacts.rear] },
    packingEvents: { front: [...packingEvents.front], rear: [...packingEvents.rear] },
  };
}

export function runJackPackingAnalysisFromTelemetry(telemetryData, partialParams = {}) {
  const raw = telemetryToJackRawRows(telemetryData);
  return runJackPackingAnalysis(raw, partialParams);
}
